import { MetaProps, BENZINGA_LOGO_URL, JsonLd, formatStringToValidJsonString } from '@benzinga/seo';
import { fixedEncodeURI } from '@benzinga/utils';

const logoPNG = 'https://www.benzinga.com/next-assets/images/schema-publisher-logo-benzinga.png';
const publisherLogoWidth = 625;
const publisherLogoHeight = 88;

const decodeDescription = (text: string) => {
  return text ?? '';
};

const organizationMetaSchema = () => {
  return {
    '@type': 'Organization',
    logo: {
      '@type': 'ImageObject',
      url: `${logoPNG}`,
      width: publisherLogoWidth,
      height: publisherLogoHeight,
    },
    name: 'Benzinga',
    url: 'https://www.benzinga.com',
  };
};

function structuredDataPage({
  author,
  authorURL,
  canonical,
  dateCreated,
  dateUpdated,
  description,
  image,
  isNotAccessibleForFree,
  structuredData,
  title,
}: MetaProps) {
  author = author || 'Benzinga';
  dateCreated = dateCreated || '2010-01-01T00:00:00Z';
  dateUpdated = dateUpdated || '2010-01-01T00:00:00Z';
  image = image || BENZINGA_LOGO_URL;
  const pageType = structuredData?.pageType || 'WebPage';
  const keywords = structuredData?.keywords || [];
  const mentions = structuredData?.mentions || [];

  title = title?.substring(0, 105);

  const cleanQuote = (text: string | null) => {
    return text ? text.replace(/"/g, '&quot;') : '';
  };

  const convertQuoteToSingle = (text: string | null) => {
    return text ? text.replace(/"/g, `'`) : '';
  };

  const articleBody = structuredData?.articleBody ? formatStringToValidJsonString(structuredData.articleBody) : '';

  const authorPerson = {
    '@type': 'Person',
    name: cleanQuote(author),
    ...(authorURL && { url: authorURL }),
  };

  return {
    '@context': 'http://schema.org',
    '@type': pageType,
    author: authorPerson,
    dateCreated: dateCreated ?? null,
    dateModified: dateUpdated ?? null,
    datePublished: dateCreated ?? null,
    description: convertQuoteToSingle(decodeDescription(description)),
    headline: cleanQuote(title)?.replace(/(\r\n|\n|\r)/gm, ' '),
    image: fixedEncodeURI(image.toString()) ?? null,
    keywords: keywords,
    mainEntityOfPage: {
      '@id': canonical,
      '@type': 'WebPage',
    },
    mentions: mentions,
    publisher: organizationMetaSchema(),
    url: canonical,
    ...(structuredData?.mainEntity !== undefined && {
      mainEntity: structuredData.mainEntity,
    }),
    isAccessibleForFree: isNotAccessibleForFree ? false : true,
    ...(structuredData?.articleBody && { articleBody }),
    ...(structuredData?.about && { about: structuredData.about }),
    ...(structuredData?.articleSection && { articleSection: structuredData.articleSection }),
    ...(structuredData?.speakable && { speakable: structuredData.speakable }),
    ...(isNotAccessibleForFree && {
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: '.paywall-content',
      },
    }),
    ...(pageType === 'NewsArticle' && {
      isPartOf: {
        '@type': ['CreativeWork', 'Product'],
        name: 'Benzinga',
        productID: 'CAowneCICw:openaccess',
      },
    }),
  };
}

export function PageStructuredData({ metaProps }: { metaProps: MetaProps }) {
  const data = structuredDataPage(metaProps);
  return <JsonLd data={data} id="page-structured-data" />;
}
